<template>
  <dashboard-widget
    :is-loading="isLoading"
    is-panel>
    <template>
      <div class="latest-activity">
        <div v-if="activities.length > 0 && !isError">
            <div class="widget-header">
                <h4>{{ $t('ui.dashboard.kit_activity_label') }}</h4>
            </div>
            <div class="list">
              <div v-for="(activity, index) in activities" :key="index" class="activity-item">
                <div class="icon">
                    <user-image
                      :firstname="activity.lead.firstname"
                      :lastname="activity.lead.lastname"
                      :email="activity.lead.email"
                      :theme="activity.lead.classification"
                    />
                </div>
                <div class="content">
                    <router-link
                      class="link"
                      :to="{
                        name: 'project_lead_edit',
                        params: {
                          id: activity.lead.project_id,
                          lead_id: activity.lead.id
                        }
                      }">
                      <div>
                        {{ activity.name }}
                      </div>
                    </router-link>

                  <p class="truncate-text" v-if="activity.interactions.isOpened">
                    {{ $t('ui.dashboard.kit_activity.opened_kit') }} <span>{{ activity.business_kit_name }}</span> {{ $t('ui.dashboard.kit_activity.opened') }}
                  </p>
                  <p class="truncate-text" v-else>{{ $t('ui.dashboard.kit_activity.interacted_with') }} <span>{{ activity.business_kit_name }}</span></p>

                  <ul v-if="!activity.interactions.isOpened">
                    <li v-if="activity.interactions.clicks > 0">
                      <span>{{ $tc('ui.dashboard.kit_activity.click_content_card', activity.interactions.clicks, { count: activity.interactions.clicks }) }}</span>
                    </li>
                    <li v-if="activity.interactions.views > 0">
                      <span>{{ $tc('ui.dashboard.kit_activity.view_content_card', activity.interactions.views, { count: activity.interactions.views }) }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
        </div>

        <div v-else-if="activities.length === 0 && !isError" class="aligner">
            <div class="text-center empty-state">
              <img src="../../../../../assets/empty-kit-activity.svg">
              <p class="empty-state-margin">{{ $t('ui.dashboard.activity_list_widget.empty_state_headline') }}</p>
              <router-link class="link" :to="{ name: 'project_new' }">{{ $t('ui.dashboard.activity_list_widget.empty_state_intro') }}</router-link>
            </div>
        </div>

        <div v-if="isError">
           <div class="text-center empty-state">
              <img src="../../../../../assets/empty-kit-activity.svg">
              <p class="empty-state-margin">{{ $t('ui.dashboard.widget_error') }}</p>
            </div>
        </div>
      </div>
    </template>
  </dashboard-widget>
</template>

<script>
import map from 'lodash/fp/map'
import reject from 'lodash/fp/reject'
import flow from 'lodash/flow'
import isUndefined from 'lodash/isUndefined'
import isNull from 'lodash/isNull'
import reduce from 'lodash/reduce'
import slice from 'lodash/fp/slice'
import split from 'lodash/split'
import includes from 'lodash/includes'
import get from 'lodash/get'
import sum from 'lodash/sum'
import join from 'lodash/join'
import find from 'lodash/find'
import eq from 'lodash/eq'
import size from 'lodash/size'
import gt from 'lodash/gt'
import defaultsTo from 'lodash/defaultTo'
import cond from 'lodash/cond'
import stubTrue from 'lodash/stubTrue'

import Charts from '@/api/Charts.js'
import Api from '@/api/Api.js'
import WidgetMixin from './mixins/WidgetMixin'
import WidgetPeriodMixin from './mixins/WidgetPeriodMixin'
import UserImage from '@/components/helpers/Specific/UserImage'
import WidgetQueryMixin from './mixins/WidgetQueryMixin'
import { getLeadName } from '../../services/DashboardService.js'

const ACTIVITIES_LIMIT = 10
const INTERACTIONS_VIEW = [
  'EMAIL_OPENED',
  'VIEW_COUPON'
]
const INTERACTIONS_CLICK = [
  'VIEW_WEBSITE_COUPON',
  'SHARE_FACEBOOK_BAG',
  'SHARE_FACEBOOK_COUPON',
  'CONFIRM_REDEEM_COUPON',
  'SHARE_FACEBOOK_MESSENGER_BAG',
  'SHARE_TWITTER_BAG',
  'CTA_COUPON',
  'SHARE_TWITTER_COUPON',
  'BOOKMARK_COUPON',
  'UNBOOKMARK_COUPON',
  'SHARE_FACEBOOK_MESSENGER_COUPON'
]
const OPEN_BAG = 'OPEN_BAG'

export default {
  mixins: [WidgetMixin, WidgetPeriodMixin, WidgetQueryMixin],

  components: {
    UserImage
  },

  props: {
    identifier: {
      type: String,
      default: 'kits-activity'
    }
  },

  data: () => ({
    activities: []
  }),

  methods: {
    async refresh() {
        this.setIsLoading(true)

        try {
            let activites = await this.get(this.from(this.filter.period), this.filter.projectId)
            this.activities = this.transform(activites)
        } catch (error) {
            this.setError(error)
            this.setIsError(true)
        } finally {
            this.setIsLoading(false)
            this.eventBus.$emit('widget-loaded', this.identifier)
        }
    },

    async get (from, project) {
      return new Promise(async (resolve, reject) => {
        let charts = new Charts()

        // Temporary query more events for the case if some are archived
        // or doesn't match the actual leads
        const margin = 5

        const tuples = [
          { key: 'from', value: `"${from}"` },
          { key: 'perPage', value: ACTIVITIES_LIMIT + margin },
        ]

        if (!!project) {
          tuples.push({ key: 'projectId', value: project })
        }

        const querySchema = `
          entries {
            lead_id
            interaction_counts
            last_interaction_date
            event_id
            business_kit_name
          }

          paginationMeta {
            page
            perPage
            hasNextPage
          }
        `
        const query = this.getQuery(tuples, 'kitActivity', querySchema)
        let { kit_activity: { entries: kitActivities } } = await charts.postQuery({
          organization_id: this.filter.organizationId,
          operationName: null,
          variables: {},
          query: `query Query {
            ${query}
          }`
        })
        .catch(reject)

        // If no events returned, no need for querying API
        if (eq(size(kitActivities), 0)) {
          resolve(kitActivities)

          return
        }

        let api = new Api()
        const leadIds = join(map(({ lead_id }) => lead_id)(kitActivities))
        const leads = await api
          .getMultiple('leads', 'id', leadIds, undefined, 'true', 'true')
          .catch(reject)

        map(kitActivity => {
          // Get lead
          if (!isNull(kitActivity.lead_id)) {
            kitActivity.lead = find(leads, ['id', kitActivity.lead_id])
          }

          return kitActivity
        })(kitActivities)

        resolve(kitActivities)
      })
    },

    transform (activities) {
      return flow([
        reject(activity => isUndefined(activity.lead)),
        slice(0, ACTIVITIES_LIMIT),
        // Assign all clicks and views and determinate if bag has been opened
        map(activity => ({
            isOpenedBag: gt(activity.interaction_counts[OPEN_BAG], 0),
            interactions: {
              clicks: reduce(activity.interaction_counts, (clicks, amount, interaction) => {
                if (includes(INTERACTIONS_CLICK, interaction)) {
                  clicks += amount
                }

                return clicks
              }, 0),

              views: reduce(activity.interaction_counts, (views, amount, interaction) => {
                if (includes(INTERACTIONS_VIEW, interaction)) {
                  views += amount
                }

                return views
              }, 0)
            },

            lead: activity.lead,
            name: getLeadName(activity.lead, this.$t('ui.dashboard.latest_activity.incomplete_lead')),
            business_kit_name: defaultsTo(activity.business_kit_name, 'a Kit'),
            event_id: activity.event_id
          })
        ),

        // Determinate if there are any views or clicks.
        // @todo Try to iterate only once
        map(({ isOpenedBag, interactions: { clicks, views }, lead, name, business_kit_name, event_id }) => ({
          interactions: {
            isOpened: isOpenedBag && eq(sum([clicks, views]), 0),
            clicks,
            views
          },

          lead,
          name,
          business_kit_name,
          event_id
        }))
        ])(activities)
    }
  }
}
</script>

<style lang="sass">
.latest-activity
  h5
    margin-top: 0
    margin-bottom: 0px

  h4
    padding: 0px
    margin-top: 0
    margin-bottom: 10px !important
    text-align: left

  img
    display: inline-block
    padding-top: 4px

  .filter-lists
    display: inline-block
    float: right

  .list
    .activity-item
      padding: 12px 0px
      display: flex
      -webkit-box-flex: 1
      flex-grow: 1
      align-items: flex-start
      -webkit-box-pack: start
      justify-content: flex-start
      max-width: 100%
      width: 100%

      .icon
        box-sizing: border-box
        padding-right: 15px

      .content
        flex-basis: auto
        -webkit-box-flex: 100
        flex-grow: 100
        flex-shrink: 1
      a
        font-weight: $font-medium
        font-size: 16px
        line-height: 23px

      p
        margin-bottom: 0px
        color: $ink-light
        max-width: 344px
        span
          color: $ink-dark
      ul
        margin: 6px 0px 0px 0px
        list-style-type: none
        padding-left: 0px
        list-style-image: url("../../../../../assets/green-circle.svg")
        list-style-position: inside
        li
          font-size: 12px
          color: $ink-lighter
          font-weight: $font-medium
          span
            vertical-align: text-bottom
            line-height: 17px
  .empty-state
    h4
      text-align: inherit

    p
      color: $ink-light
</style>



// WEBPACK FOOTER //
// KitsActivityWidget.vue?b610f9f6